import {
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { Timestamp } from '@dadohr/shared/domainTypes/time';
import styled from '@emotion/styled';
import { orderBy, sortBy, truncate } from 'lodash';
import { UserIcon, UsersIcon } from 'lucide-react';
import { useMemo } from 'react';
import { ColoredChip } from '../../../../../../../components/ColoredChip';
import {
  ItemSorters,
  RowsRenderer,
  RowsRendererHeadProps,
  ROW_HEIGHTS,
  useSortQueryParam
} from '../../../../../../../components/GroupableList';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { useStringQueryParam } from '../../../../../../../routes';
import { toFullName } from '../../../../../../../services/user';
import { ROUTES_V2 } from '../../../../../../routes';
import {
  CompletedDate,
  DueDate
} from '../../../../Experience/components/Tasks';
import { InboxNavItemKey } from '../../../components/Nav/InboxNav';
import { InboxTableMode, TaskGroup } from '../types';
import { PinIconButton } from './PinButtons';
import { WithTokenPill } from './WithTokenPill';

// TODO - this is virtually identical to MessageGroupTable and could be unified

type ColumnName = 'task' | 'experience' | 'date' | 'pin';
type D = TaskGroup;
type OtherProps = {
  userAccessToken: string;
  mode: InboxTableMode;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedUsers?: string[] | null;
  selectedTemplates?: string[] | null;
  highlightOverdue?: boolean;
  tsort?: string;
};
type Column = IColumn<D, ColumnName, OtherProps>;

const getEarliestDueDate = (g: TaskGroup): Timestamp | null => {
  const sorted = sortBy(g.tasks, (t) => t.timing.end.toMillis());
  return sorted[0]?.timing.end || null;
};

const getEarliestCompletedAt = (g: TaskGroup): Timestamp | null => {
  const sorted = sortBy(g.tasks, (t) => t.completedAt?.toMillis() || Infinity);
  return sorted[0]?.completedAt || null;
};

const GroupTitleGrid = styled('div')((p) => ({
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'max-content 55px 1fr',
  gridColumnGap: p.theme.spacing(1),
  alignItems: 'center',
  whiteSpace: 'nowrap'
}));

const ICON_SIZE = 20;
const GroupTitle = ({ g, mode }: { g: TaskGroup; mode: InboxTableMode }) => {
  const { t } = useUnsafeTranslation();
  const isSingle = g.tasks.length === 1;
  const label =
    isSingle && g.tasks[0].actorKey === PARTICIPANT_ACTOR_KEY
      ? t('Me')
      : g.tasks.length;

  return (
    <GroupTitleGrid>
      {isSingle ? (
        <UserIcon size={ICON_SIZE} />
      ) : (
        <UsersIcon size={ICON_SIZE} />
      )}
      <ColoredChip color="gray" mode="light">
        {label}
      </ColoredChip>
      <WithTokenPill
        d={isSingle ? g.tasks[0].taskTitleInterpolated : g.taskTitle}
      />
    </GroupTitleGrid>
  );
};

const EXPERIENCE_COLUMN: Column = {
  key: 'experience',
  head: () => 'Experience',
  cell: (d) => <WithTokenPill d={d.templateTitle} />,
  align: 'left',
  sortable: true,
  width: 150,
  flexGrow: 1,
  noWrap: true
};

const DATE_COLUMN: Column = {
  key: 'date',
  head: (o) => (o?.selectedGroup === 'complete' ? 'Completed at' : 'Due date'),
  cell: (d, o) => {
    if (o.selectedGroup === 'complete') {
      const completedAt = getEarliestCompletedAt(d);
      return completedAt ? <CompletedDate ts={completedAt} short /> : <div />;
    }
    const dueDate = getEarliestDueDate(d);
    const showOverdue =
      o.highlightOverdue && d.tasks.some((t) => t.status === TaskStatus.LATE);
    return dueDate ? (
      <DueDate ts={dueDate} highlightAsOverdue={showOverdue} />
    ) : (
      <div />
    );
  },
  sortable: true,
  align: 'right',
  width: 120,
  flexGrow: 0
};

const PIN_COLUMN: Column = {
  key: 'pin',
  head: () => '',
  cell: (d, o) => (
    <PinIconButton
      userAccessToken={o.userAccessToken}
      type="TASK"
      templateId={d.templateId}
      itemId={d.taskTemplateId}
    />
  ),
  align: 'right',
  sortable: false,
  width: 50,
  flexGrow: 0
};

const COLUMNS_WITH_SUPPORTING_ACTOR: Column[] = [
  {
    key: 'task',
    head: () => 'Task',
    cell: (d, o) => <GroupTitle g={d} mode={o.mode} />,
    align: 'left',
    width: 300,
    sortable: true,
    flexGrow: 5,
    noWrap: true
  },
  EXPERIENCE_COLUMN,
  DATE_COLUMN
];

const COLUMNS_PARTICIPANT_ONLY: Column[] = [
  {
    key: 'task',
    head: () => 'Task',
    // PARTICIPANT view: variables ALWAYS resolve (product spec 2026-07-13).
    // `taskTitle` is the humanized NON-interpolated title (for batch/supporting-
    // actor views with no single-participant context); a participant's own rows
    // always have their context, so render the interpolated title. All tasks in
    // a participantOnly group belong to the same participant -> tasks[0].
    cell: (d) => <WithTokenPill d={d.tasks[0]?.taskTitleInterpolated ?? d.taskTitle} />,
    align: 'left',
    width: 300,
    sortable: true,
    flexGrow: 5,
    noWrap: true
  },
  EXPERIENCE_COLUMN,
  DATE_COLUMN
];

const COLUMNS_SINGLE_USER: Column[] = [
  {
    key: 'task',
    head: () => 'Task',
    cell: (d) => {
      const tsk = d.tasks[0];
      return (
        <FlexContainer style={{ whiteSpace: 'nowrap' }}>
          <ColoredChip color="gray" mode="light">
            {truncate(toFullName(tsk.participant), { length: 30 })}
          </ColoredChip>

          <WithTokenPill d={tsk.taskTitleInterpolated} />
        </FlexContainer>
      );
    },
    align: 'left',
    width: 300,
    sortable: true,
    flexGrow: 4
  },
  EXPERIENCE_COLUMN,
  DATE_COLUMN
];

export const TASK_GROUP_TABLE_SORT_GETTERS: Record<
  string,
  (d: TaskGroup, g?: InboxNavItemKey | null) => string | number
> = {
  task: (d) => d.taskTitle,
  experience: (d) => d.templateTitle,
  date: (d, g) =>
    (g === 'complete'
      ? getEarliestCompletedAt(d)?.toMillis()
      : getEarliestDueDate(d)?.toMillis()) || Infinity
};

const SORTERS: ItemSorters<D, OtherProps> = {
  task: {
    key: 'task',
    items: { sort: (d) => TASK_GROUP_TABLE_SORT_GETTERS.task(d), dir: 'asc' }
  },
  experience: {
    key: 'experience',
    items: {
      sort: (d) => TASK_GROUP_TABLE_SORT_GETTERS.experience(d),
      dir: 'asc'
    }
  },

  date: {
    key: 'date',
    items: {
      sort: (d, o) => TASK_GROUP_TABLE_SORT_GETTERS.date(d, o.selectedGroup),
      dir: 'asc'
    }
  }
};

const TASK_GROUP_TABLE_DEFAULT_SORTER = SORTERS.date;

export const sortTaskGroups = (
  taskGroups: TaskGroup[],
  key: string | null | undefined,
  dir: 'asc' | 'desc',
  g: InboxNavItemKey | null
): TaskGroup[] => {
  const sorter =
    TASK_GROUP_TABLE_SORT_GETTERS[key || ''] ||
    TASK_GROUP_TABLE_SORT_GETTERS[TASK_GROUP_TABLE_DEFAULT_SORTER.key];
  return orderBy(taskGroups, (d) => sorter(d, g), dir);
};

const ROW_TO_KEY = (d: D) => d.templateId + '|' + d.taskTemplateId;

const ROW_TO_HREF = (d: D, o: OtherProps) => {
  if (d.tasks.length === 0) {
    return null;
  }
  if (d.tasks.length === 1) {
    const task = d.tasks[0];
    return ROUTES_V2.inboxTask.url(
      o.userAccessToken,
      task.taskTemplateId,
      task.instanceAccessToken,
      {
        returnTo: o.returnTo,
        g: o.selectedGroup || undefined,
        u: o.selectedUsers,
        t: o.selectedTemplates,
        tsort: o.tsort
      }
    );
  }
  return ROUTES_V2.inboxTasks.url(o.userAccessToken, d.taskTemplateId, {
    returnTo: o.returnTo,
    g: o.selectedGroup || undefined,
    u: o.selectedUsers,
    t: o.selectedTemplates,
    tsort: o.tsort
  });
};

const HEAD_PROPS: RowsRendererHeadProps = {
  transparent: true,
  noTopPadding: true
};

export const TASK_GROUP_TABLE_SORT_QUERY_PARAM = 'tsort';

export const TaskGroupTable = ({
  groups,
  mode,
  userAccessToken,
  returnTo,
  selectedGroup,
  selectedUsers,
  selectedTemplates,
  highlightOverdue,
  includePinColumn
}: {
  groups: TaskGroup[];
  mode: InboxTableMode;
  userAccessToken: string;
  returnTo: string;
  selectedGroup?: InboxNavItemKey;
  selectedTemplates?: string[] | null;
  selectedUsers?: string[] | null;
  highlightOverdue?: boolean;
  includePinColumn?: boolean;
}) => {
  // TODO Should be moved further up i think
  const [[sorter, dir], setSort] = useSortQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    SORTERS
  );
  const [rawSortParam] = useStringQueryParam(
    TASK_GROUP_TABLE_SORT_QUERY_PARAM,
    ''
  );
  const otherProps = useMemo<OtherProps>(() => {
    return {
      userAccessToken,
      mode,
      returnTo,
      selectedGroup,
      selectedUsers,
      selectedTemplates,
      highlightOverdue,
      tsort: rawSortParam
    };
  }, [
    mode,
    userAccessToken,
    returnTo,
    selectedGroup,
    selectedUsers,
    selectedTemplates,
    highlightOverdue,
    rawSortParam
  ]);
  const columns = useMemo(() => {
    const _cs =
      mode === 'participantOnly'
        ? COLUMNS_PARTICIPANT_ONLY
        : mode === 'singleUser'
        ? COLUMNS_SINGLE_USER
        : COLUMNS_WITH_SUPPORTING_ACTOR;
    return includePinColumn ? [..._cs, PIN_COLUMN] : _cs;
  }, [mode, includePinColumn]);
  const sorterOrDefault = sorter || TASK_GROUP_TABLE_DEFAULT_SORTER;
  return (
    <RowsRenderer
      fullWidth
      variant="contained"
      columns={columns}
      rows={groups}
      rowToKey={ROW_TO_KEY}
      rowToHref={ROW_TO_HREF}
      sorter={sorterOrDefault}
      sortDirection={dir}
      renderHead={true}
      headProps={HEAD_PROPS}
      onHeadClick={(c, d) =>
        setSort([SORTERS[c.key] || TASK_GROUP_TABLE_DEFAULT_SORTER, d])
      }
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};
